import React, { useState, useRef } from 'react';
import cn from 'classnames';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import Tooltip from '../../General/Tooltip';

export const Spinner = ({ color = '#767676' }) => (
  <svg className='nb-spinner' width='13' height='13' viewBox='0 0 14 14'>
    <circle cx='7' cy='7' r='5.5' stroke={color} strokeWidth='2' fill='none' strokeDasharray='22' strokeDashoffset='8' strokeLinecap='round' />
  </svg>
);

export const FilterSection = ({ label, badge, collapsible = false, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className='nb-section'>
      <div
        className={cn('nb-section-header', { collapsible })}
        onClick={collapsible ? () => setOpen(p => !p) : undefined}
      >
        <div className='nb-section-title'>
          <span className='nb-section-title-text'>{label}</span>
          {badge && <span className='nb-new-badge'>{badge}</span>}
        </div>
        {collapsible && <span className={cn('nb-chevron', { open })}>▾</span>}
      </div>
      {(!collapsible || open) && <div className='nb-section-body'>{children}</div>}
    </div>
  );
};

// Radio group over `{ value, label, desc? }` options. An option with a `desc` renders an info
// tooltip beside its label (used by the Purchase Activity filter); options without one don't.
export const RadioList = ({ name, options, value, onChange }) => (
  <div className='nb-radio-list'>
    {options.map(opt => (
      <label key={opt.value}>
        <input type='radio' name={name} value={opt.value} checked={value === opt.value} onChange={() => onChange(opt.value)} />
        {opt.label}
        {opt.desc && (
          <Tooltip message={opt.desc} position='right' size='regular'>
            <span className='nb-info-icon' aria-label={opt.desc}>
              i
            </span>
          </Tooltip>
        )}
      </label>
    ))}
  </div>
);

// Segmented single-select over `[value, label]` option tuples (the `.nb-segmented` button row).
export const SegmentedControl = ({ options, value, onChange }) => (
  <div className='nb-segmented'>
    {options.map(([v, l]) => (
      <button key={v} className={cn({ active: value === v })} onClick={() => onChange(v)}>
        {l}
      </button>
    ))}
  </div>
);

// Multi-select filter (react-select) over `{ value, label }` options, with an empty-state message
// when there's nothing to choose from. onChange receives the raw selected values.
export const MultiSelectFilter = ({ className = 'nb-code-select', placeholder, emptyText, options, value, onChange }) =>
  options.length === 0 ? (
    <p className='nb-hint'>{emptyText}</p>
  ) : (
    <Select
      isMulti
      className={className}
      classNamePrefix='nb-code-select'
      placeholder={placeholder}
      options={options}
      value={value}
      onChange={opts => onChange((opts || []).map(o => o.value))}
    />
  );

// Async multi-select for catalogs too large to ship up front (e.g. brands). `loadOptions(input)`
// resolves `{ value, label }[]` from the server; `value` is the selected option objects; `onChange`
// receives the full selected option objects so the caller can keep labels for what's chosen.
export const AsyncMultiSelectFilter = ({ className = 'nb-code-select', placeholder, loadOptions, value, onChange }) => (
  <AsyncSelect
    isMulti
    cacheOptions
    defaultOptions
    className={className}
    classNamePrefix='nb-code-select'
    placeholder={placeholder}
    loadOptions={loadOptions}
    value={value}
    onChange={opts => onChange(opts || [])}
  />
);

export const RangeSlider = ({ value, onChange }) => {
  const fillStyle = { left: `${value[0]}%`, right: `${100 - value[1]}%` };
  return (
    <div className='nb-slider'>
      <div className='nb-slider-track'>
        <div className='nb-slider-fill' style={fillStyle} />
      </div>
      {[0, 1].map(i => (
        <input
          key={i}
          type='range'
          min={0}
          max={100}
          value={value[i]}
          // With both handles at 100 the max thumb (normally on top) is pinned by its own clamp and
          // covers the min thumb, deadlocking the slider — raise the min thumb so it stays grabbable.
          style={i === 0 && value[0] === 100 ? { zIndex: 1 } : undefined}
          onChange={e => {
            const v = +e.target.value;
            onChange(i === 0 ? [Math.min(v, value[1]), value[1]] : [value[0], Math.max(v, value[0])]);
          }}
        />
      ))}
    </div>
  );
};

export const SlideOver = ({ title, subtitle, onClose, className, resizable = false, defaultWidth = null, children }) => {
  // null = use the CSS default width; a defaultWidth opens the panel wider out of the gate, and
  // once dragged an explicit pixel width takes over. Clamp so it never exceeds the viewport.
  const [width, setWidth] = useState(() => (defaultWidth ? Math.min(defaultWidth, window.innerWidth - 80) : null));
  const draggingRef = useRef(false);

  const startResize = e => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.userSelect = 'none';
    const onMove = ev => {
      if (!draggingRef.current) return;
      // Panel is anchored to the right edge, so width grows as the cursor moves left.
      const next = window.innerWidth - ev.clientX;
      setWidth(Math.min(Math.max(next, 360), window.innerWidth - 80));
    };
    const onUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  return (
    <>
      <div className='nb-slideover-backdrop' onClick={onClose} />
      <div className={cn('nb-slideover', className)} style={width ? { width, maxWidth: 'none' } : undefined}>
        {resizable && <div className='nb-slideover-resizer' onMouseDown={startResize} title='Drag to resize' />}
        <div className='nb-slideover-header'>
          <div className='nb-slideover-header-row'>
            <div>
              <h3>{title}</h3>
              {subtitle && <p>{subtitle}</p>}
            </div>
            <button className='nb-slideover-close' onClick={onClose}>
              ×
            </button>
          </div>
        </div>
        <div className='nb-slideover-body'>{children}</div>
      </div>
    </>
  );
};

export const StatLine = ({ label, val, pct }) => (
  <div className='nb-stat'>
    <span className='nb-stat-val'>{val}</span>
    {pct !== undefined && <span className='nb-stat-pct'>({pct}%)</span>}
    <span className='nb-stat-label'>{label}</span>
  </div>
);

export const ResultBlock = ({ label, data, total }) => (
  <div className='nb-result-block'>
    <p className='nb-result-block-label'>{label}</p>
    <StatLine label='Texted Noir' val={data.texted} pct={total ? Math.round((data.texted / total) * 100) : 0} />
    <StatLine label='Submitted request' val={data.requested} pct={total ? Math.round((data.requested / total) * 100) : 0} />
    <StatLine label='Converted (Noir purchase)' val={data.converted} pct={total ? Math.round((data.converted / total) * 100) : 0} />
  </div>
);
